import { Controller } from '@hotwired/stimulus';
import AutoSave from 'stimulus/auto_save';

export default class extends Controller {
  static targets = ['input', 'clear', 'message'];

  static values = {
    autoSaveUrl: String,
    messageId: String,
    debounce: { type: Number, default: 500 }
  };

  connect() {
    this.toggleClear();
    this.autoSave = new AutoSave({
      url: this.autoSaveUrlValue,
      name: this.inputTarget.name,
      debounce: this.debounceValue,
      label: this.identifier,
      element: this.element,
      onSaved: value => this.dispatch('saved', { detail: { value } }),
      onError: message => this.showError(message)
    });
  }

  disconnect() {
    this.autoSave.cancel();
  }

  // ── Public actions (wired from data-action) ──────────────────────────────
  handleInput() {
    this.clearError();
    this.toggleClear();
  }

  handleChange() {
    if (this.autoSaveUrlValue) this.autoSave.saveNow(this.inputTarget.value);
  }

  clear() {
    if (this.inputTarget.disabled) return;
    this.inputTarget.value = '';
    this.inputTarget.focus();
    this.inputTarget.dispatchEvent(new Event('input', { bubbles: true }));
    this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }));
    this.dispatch('clear');
  }

  // ── Clear button ──────────────────────────────────────────────────────────
  toggleClear() {
    if (!this.hasClearTarget) return;
    this.clearTarget.hidden = this.inputTarget.value === '';
  }

  // ── Error / hint region ───────────────────────────────────────────────────
  // The hint is a Ui::HintComponent; its text lives in the `[data-hint-text]`
  // span and the error icon/colour swap is CSS-driven by the wrapper's
  // `--error` modifier.
  showError(message) {
    this.element.classList.add('ui-input-field--error');
    this.element.dataset.inputFieldErrorActive = 'true';
    this.inputTarget.setAttribute('aria-invalid', 'true');
    if (!this.hasMessageTarget) return;

    this.setMessageText(message);
    this.messageTarget.setAttribute('role', 'alert');
    this.messageTarget.hidden = false;
    this.describeByMessage(true);
  }

  clearError() {
    if (!this.element.classList.contains('ui-input-field--error')) return;

    this.element.classList.remove('ui-input-field--error');
    delete this.element.dataset.inputFieldErrorActive;
    this.inputTarget.setAttribute('aria-invalid', 'false');
    if (!this.hasMessageTarget) return;

    // Server-rendered errors carry the variant class and role on the hint
    // itself; drop them so the restored hint renders as a neutral hint.
    this.messageTarget.classList.remove('ui-hint--error');
    this.messageTarget.removeAttribute('role');
    const hint = this.element.dataset.inputFieldHint || '';
    this.setMessageText(hint);
    this.messageTarget.hidden = hint === '';
    this.describeByMessage(hint !== '');
  }

  setMessageText(text) {
    const textEl = this.messageTarget.querySelector('[data-hint-text]');
    if (textEl) textEl.textContent = text;
  }

  // React rebuilds `aria-describedby` from the regions it renders, so the
  // message id must come and go with the visible message.
  describeByMessage(present) {
    const ids = (this.inputTarget.getAttribute('aria-describedby') || '')
      .split(' ')
      .filter(id => id && id !== this.messageIdValue);

    if (present) ids.unshift(this.messageIdValue);

    if (ids.length) {
      this.inputTarget.setAttribute('aria-describedby', ids.join(' '));
    } else {
      this.inputTarget.removeAttribute('aria-describedby');
    }
  }
}
